import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "@/layouts/MainLayout";
import AuthService from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Loader2, Search, UserPlus, Mail, Calendar, ShieldCheck, Edit2 } from "lucide-react";
import { formatDate } from "@/lib/utils";

interface StaffMember {
  id: string;
  name: string;
  email: string;
  role?: string;
  created_at?: string;
  auth_uuid?: string;
  associated_branch?: string | number;
}

interface RoleMetadata {
  id: number;
  name: string;
  is_clinical: boolean;
}

export default function StaffList() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [rolesMetadata, setRolesMetadata] = useState<RoleMetadata[]>([]);
  const [hospitalData, setHospitalData] = useState<any[]>([]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Resolve current user profile, roles metadata, and hospital data in parallel.
      const [user, roles, hospitals] = await Promise.all([
        AuthService.getCurrentUser(),
        AuthService.getRolesMetadata(),
        AuthService.getHospitalsAndBranches(),
      ]);

      setRolesMetadata(roles || []);
      setHospitalData(hospitals || []);

      let currentProfile = null;
      if (user?.username) {
        currentProfile = await AuthService.getProfile(user.username);
        setCurrentUser(currentProfile);
      }

      console.log('🔍 [StaffList] Caller role:', currentProfile?.role,
        '| branch:', currentProfile?.associated_branch);

      // ── Single scoped fetch ───────────────────────────────────────────────
      // Call GET /rds/staff-users/role with NO role filter.
      // The backend enforces caller-scoped results:
      //   Admin       → all staff system-wide
      //   BranchAdmin → only staff with the same associated_branch as the caller
      //   Others      → staff whose location_ids overlap the caller's
      //
      // This replaces the previous pattern of looping per-role and filtering
      // client-side, which allowed cross-branch staff to leak through.
      const allStaff = await AuthService.getStaffByRole('');

      console.log('🔍 [StaffList] Staff returned by backend (scoped):', allStaff.length);

      const finalStaff = allStaff.map((s: any) => ({
        ...s,
        name: s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.email || 'Unknown Staff',
      }));

      setStaff(finalStaff);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getBranchName = (branchId: string | number | undefined) => {
    if (!branchId) return "Not Assigned";
    const bId = String(branchId);

    for (const item of hospitalData) {
      const clinics = item.clinic_locations || item.locations || item.branches || [];
      const branch = clinics.find((b: any) => String(b.id || b.branch_id || b.location_id) === bId);

      if (branch) {
        const hName = branch.hospital_name || item.hospital_name || item.hospitalName || "Hospital";
        const bName = branch.name || branch.branch_name || branch.branchName || "Branch";
        return `${hName} - ${bName}`;
      }
    }
    return `ID: ${bId}`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const isClinical = (roleName: string) => {
    const role = rolesMetadata.find(r =>
      ((r as any).role || (r as any).name || '').toLowerCase() === (roleName || '').toLowerCase()
    );
    return role?.is_clinical === true;
  };

  const canEditRole = (member: StaffMember) => {
    if (!currentUser) return false;
    const normalizedUserRole = currentUser.role?.toLowerCase().replace(/[\s_]/g, '') || '';
    const isSuperAdmin = normalizedUserRole === 'admin' || currentUser.role_id === 1;

    // Admins and BranchAdmins can edit staff
    if (isSuperAdmin || normalizedUserRole === 'branchadmin') {
      // Don't let users edit their own role/details from here (use profile page)
      if (member.auth_uuid === currentUser.auth_uuid) return false;
      return true;
    }

    return false;
  };


  const filteredStaff = staff.filter(member =>
    member.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    member.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#f7f8fa] p-6 lg:p-10 font-sans">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div>
              <h1 className="text-3xl lg:text-4xl font-extrabold text-[#1a7a4a] tracking-tight">
                Staff Members
              </h1>
              <p className="text-gray-500 mt-2 text-lg">
                Overview of all registered staff and healthcare providers
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="relative group">
                <input
                  type="text"
                  placeholder="Search staff members..."
                  className="pl-12 pr-4 h-12 w-64 lg:w-80 bg-white border-gray-200 focus:border-[#1a7a4a] focus:ring-[#1a7a4a] rounded-2xl text-sm transition-all shadow-sm group-hover:shadow-md outline-none"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Search className="absolute left-4 top-3.5 h-5 w-5 text-gray-400 group-hover:text-[#1a7a4a] transition-colors" />
              </div>
            </div>
          </div>

          <Card className="rounded-3xl border-gray-100 shadow-xl shadow-gray-200/50 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              {loading ? (
                <div className="p-20 flex flex-col items-center justify-center space-y-4">
                  <Loader2 className="h-10 w-10 animate-spin text-[#1a7a4a]" />
                  <p className="text-gray-500 font-medium animate-pulse">Fetching staff records...</p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-gray-50/50 border-b border-gray-100">
                      <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500">Staff Member</th>
                      <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500">System Identifiers</th>
                      <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500">Role</th>
                      <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500">Branch / Hospital</th>
                      <th className="px-6 py-5 text-xs font-bold uppercase tracking-wider text-gray-500">Joined Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredStaff.map((member) => (
                      <tr
                        key={member.id}
                        className={`group transition-colors ${canEditRole(member) ? 'hover:bg-[#1a7a4a]/5 cursor-pointer' : 'cursor-default'}`}
                        onClick={() => {
                          if (canEditRole(member)) {
                            navigate(`/admin/staff/${member.id}`);
                          }
                        }}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1a7a4a] to-[#2d5a3d] flex items-center justify-center text-white font-bold text-lg shadow-sm group-hover:scale-110 transition-transform duration-200">
                              {member.name?.[0] || "?"}
                            </div>
                            <div>
                              <div className="font-bold text-gray-900 group-hover:text-[#1a7a4a] transition-colors">{member.name}</div>
                              <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
                                <Mail className="w-3 h-3 text-gray-400" />
                                {member.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-1">
                            <div className="text-[11px] font-mono text-gray-500 bg-gray-50 px-2 py-0.5 rounded-md inline-block">ID: {member.id}</div>
                            {member.auth_uuid && (
                              <div className="text-[10px] font-mono text-gray-400 pl-2">Auth: {member.auth_uuid}</div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2 group/role">
                            <span className={`inline-flex items-center px-3 py-1 rounded-xl text-[11px] font-bold border uppercase tracking-tight shadow-sm ${isClinical(member.role || '')
                              ? 'bg-blue-50 text-blue-700 border-blue-100'
                              : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                              }`}>
                              {isClinical(member.role || '') ? (
                                <ShieldCheck className="w-3.5 h-3.5 mr-1.5 text-blue-500" />
                              ) : (
                                <UserPlus className="w-3.5 h-3.5 mr-1.5 text-emerald-500" />
                              )}
                              {member.role}
                            </span>
                            {canEditRole(member) && (
                              <button
                                onClick={() => navigate(`/admin/staff/${member.id}`)}
                                className="ml-2 p-1.5 rounded-lg bg-gray-50 text-gray-400 hover:text-[#1a7a4a] hover:bg-emerald-50 transition-all shadow-sm border border-transparent hover:border-emerald-100"
                                title="Edit Staff Member"
                              >
                                <Edit2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="font-medium text-gray-700">
                            {getBranchName(member.associated_branch)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex items-center gap-2">
                            <div className="p-1.5 rounded-lg bg-gray-50 group-hover:bg-white transition-colors">
                              <Calendar className="w-4 h-4 text-gray-400" />
                            </div>
                            <span className="font-medium">{formatDate(member.created_at)}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {!loading && filteredStaff.length === 0 && (
              <div className="p-24 text-center">
                <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6 shadow-inner">
                  <UserPlus className="w-12 h-12 text-gray-200" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">No results matching your search</h3>
                <p className="text-gray-500 max-w-sm mx-auto">We couldn't find any staff members matching that search term. Please try a different name or email.</p>
              </div>
            )}
          </Card>
        </div>
      </div>
    </MainLayout>
  );
}
